import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import type { Delivery, InventoryLevel, UntrackedMaterial } from '@interfaces/ILogistics';
import { PackageIcon } from '@utils/icons';
import { EmptyStateTile } from '@components/ui/EmptyStateTile/EmptyStateTile';
import { Panel } from '../Panel';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { InventoryRow, type IInventoryRowActions } from './InventoryRow';
import { NoMatches } from './NoMatches';
import { InventoryUntrackedRow } from './InventoryUntrackedRow';
import { useInventoryDueDismissed } from './useInventoryDueDismissed';
import { incomingAmountFor } from '../lines';
import styles from './InventoryLevels.module.css';

interface IInventoryLevelsProps extends IInventoryRowActions {
  inventory: InventoryLevel[];
  /** The site's delivery orders — read only to resolve a row's incoming
   *  amount to the LINE for that material (`incomingAmountFor`), so a truck
   *  carrying three never quotes the other two at this row. */
  deliveries: Delivery[];
  onAdd: () => void;
  /** The row's one affordance — open the material's detail drawer (edit and
   *  delete live there now, not on a kebab). */
  onOpenDetail: (level: InventoryLevel) => void;
  /** Renewal beats arrival beats the order prompt (spec §4) — fired from the
   *  row's own Renew action when a landed delivery (or an expired estimate)
   *  needs confirming. Opens the stock dialog prefilled; never writes
   *  directly. */
  /** Materials with an order but no stock record. Rendered as placeholder rows
   *  above the tracked ones — see `UntrackedMaterial`. */
  untracked: UntrackedMaterial[];
  /** Record stock for one of them: opens the stock dialog seeded from the
   *  delivery, so all that is left to type is how long it should last. */
  onAddStock: (material: UntrackedMaterial) => void;
  /** The active site — scopes the persisted "dismissed arrival asks" so one
   *  site's dismissals never hide another's (delivery refs restart per site). */
  siteId: string;
  /** The PROJECT's IANA timezone — the calendar every day-level reading here
   *  is measured against. */
  tz: string;
  /** The Materials segment's unified search has a query. An empty list then
   *  means "no matches", not "no stock yet" — the guided first-material CTA
   *  would be a lie, so a plain no-match line renders instead. */
  searchActive?: boolean;
  /** The caller's role may write (the page's `canEdit`). When false every
   *  action on the panel and its rows is not rendered. Opening a row's
   *  drawer and jumping to a delivery stay live — they read, they do not
   *  write. */
  canEdit: boolean;
}

/**
 * Real self-reported stock (snapshot + estimate). Rows arrive derived and
 * urgency-sorted from the backend; this component only renders the list —
 * a single tracked row is `InventoryRow`, a single untracked placeholder is
 * `InventoryUntrackedRow`. Honesty rules (spec §3): every figure is labeled
 * an estimate, and an exhausted estimate reads "needs checking" — never "0d
 * left".
 */
export function InventoryLevels({
  inventory,
  deliveries,
  onAdd,
  onOpenDetail,
  onSync,
  onRenewArrived,
  onOrderNow,
  onJumpToDelivery,
  untracked,
  onAddStock,
  deletingMaterial,
  siteId,
  tz,
  searchActive = false,
  canEdit,
}: IInventoryLevelsProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  const { dueDismissed, dueKey, dismissDue } = useInventoryDueDismissed(siteId, tz);

  return (
    <Panel
      fill
      tour="logistics-inventory"
      title={t('logistics.inventory.title')}
      subtitle={t('logistics.inventory.subtitle')}
      headerRight={
        // Whenever the list renders at all. Gating on tracked rows alone left
        // an untracked-only panel with no entry point anywhere: the centered
        // empty state (which carries its own CTA) only renders when BOTH lists
        // are empty, so a panel of placeholder rows had neither button.
        canEdit && (inventory.length > 0 || untracked.length > 0 || searchActive) ? (
          <button type="button" className={styles.addBtn} onClick={onAdd} disabled={locked}>
            {t('logistics.inventory.addStock')}
          </button>
        ) : null
      }
    >
      {inventory.length === 0 && untracked.length === 0 && searchActive ? (
        <NoMatches />
      ) : inventory.length === 0 && untracked.length === 0 ? (
        /* The shared guided-empty tile (`ui/EmptyStateTile`), with this
           panel's own CTA button inside it. */
        <EmptyStateTile
          icon={<PackageIcon size={22} />}
          title={t('logistics.inventory.empty.title')}
          body={t('logistics.inventory.empty.body')}
        >
          {canEdit ? (
            <button type="button" className={styles.addBtn} onClick={onAdd} disabled={locked}>
              {t('logistics.inventory.empty.cta')}
            </button>
          ) : null}
        </EmptyStateTile>
      ) : (
        <ScrollArea className={styles.list}>
          <ul className={styles.items}>
            {/* Materials that exist on the delivery side only, above the tracked
                rows. They carry no days-left, so they cannot take part in the
                urgency sort at all — and an unknown outranks a known shortage. */}
            {untracked.map((material) => (
              <InventoryUntrackedRow
                key={`untracked:${material.materialName}`}
                material={material}
                tz={tz}
                onJumpToDelivery={onJumpToDelivery}
                onAddStock={onAddStock}
                canEdit={canEdit}
              />
            ))}
            {inventory.map((level) => (
              <InventoryRow
                key={level.materialName}
                level={level}
                incomingAmount={incomingAmountFor(deliveries, level)}
                tz={tz}
                onOpenDetail={onOpenDetail}
                onSync={onSync}
                onRenewArrived={onRenewArrived}
                onOrderNow={onOrderNow}
                onJumpToDelivery={onJumpToDelivery}
                deletingMaterial={deletingMaterial}
                dueDismissed={dueDismissed}
                dueKey={dueKey}
                dismissDue={dismissDue}
                canEdit={canEdit}
              />
            ))}
          </ul>
        </ScrollArea>
      )}
    </Panel>
  );
}
